// UpNextInline — H1-Phase-1-Track-2-Sub-2.2.
//
// "Keep scrolling"-Rail nach Colophon, full-width, part of the flow.
// Source-of-truth: docs/mockups/viewer/Scrolly_Platform-2.html:2022-2071
// `function UpNextInline`.
//
// Distinct vom alten inline "Weitere Scrollys"-Section in ViewerChrome
// (vorher Teil der Colophon, max 3 Cards, Creator/Category-Filter): jetzt
// eigenständige Section nach Colophon mit bis zu 5 Cards via Mockup-
// Slice-Pattern.
//
// Variant-Map: "bold" implementiert (matches aktueller hardcoded
// Colophon-Dark-Background). "subtle" via colophonStyle-Prop wäre
// Map-2-Variant-Migration; aktuell Plan-2-Backlog (Bold default).
//
// Block 2.5 TEIL C (2026-05-22): Data-Source-Refactor.
// VORHER: ALL_SCROLLYS-Mock-Filter mit self-exclude + Top-5-Slice
//         (= Dummy-Daten aus Mock-Liste).
// JETZT:  `items`-Prop (caller-provided), Rail wird ausgeblendet wenn
//         leer. Briefing-Zitat: "Wenn keine Nachbarn vorhanden:
//         Rail leer lassen oder ausblenden, NICHT mit Dummy füllen."
//
// PLAN-2-BACKLOG: Public-Listing-Endpoint für echte Org-Nachbarn.
// Heutiger Stand: kein `GET /public/scrollys/by-org/:orgSlug`-Endpoint
// existiert (verifiziert 2026-05-22 durch grep über alle `@Public()`-
// Controller in src/). ViewerChrome passt heute `items={[]}` durch,
// daher ist die Rail im Public-Viewer aktuell ausgeblendet.
// Sobald Backend-Endpoint live ist: Hook `useOrgNachbarn(orgSlug)` mit
// TanStack-Query bauen und `items={hookResult.data ?? []}` reichen.

import type { MockScrolly } from "../../lib/mock/scrollys";
import { PINK } from "../../lib/tokens";
import { UpNextCard, type UpNextCardVariant } from "./UpNextCard";
import { viewerT as t } from '@scrolly/viewer';
import type { Lang } from '@scrolly/viewer';

export type UpNextInlineProps = {
  /**
   * Block 2.5 TEIL C: caller-provided Items. Wenn leer / undefined →
   * Rail wird ausgeblendet (return null). KEIN Mock-Fallback mehr.
   */
  items?: MockScrolly[];
  /**
   * Breitenmodus für Layout-Spacing. Default `true` (Desktop-Layout
   * mit 80px-Padding + 44px-Headline + 220-260px-Card-Width).
   * `false` aktiviert Mobile-Layout (48px-Padding + 32px-Headline +
   * 160-180px-Card-Width).
   *
   * H1-Phase-1-Track-2: heute immer wide (kein Mobile-Layout-Switch
   * in ViewerChrome). Plan-2-Backlog: Mobile-Cuts wide=false bei
   * viewport-width < 768.
   */
  wide?: boolean;
  /**
   * Variant-Inheritance von Colophon. Default `bold` (matches heutigen
   * Colophon-Hardcoded-Dark-Background in ViewerChrome.tsx:1052).
   * "subtle"-Migration ist Plan-2-Backlog (colophonStyle-Prop in
   * ScrollyViewer + Variant-Pipeline).
   */
  colophonStyle?: UpNextCardVariant;
  lang?: Lang;
};

type ContainerTokens = {
  bg: string;
  fg: string;
  muted: string;
  rule: string;
};

const CONTAINER_TOKENS: Record<UpNextCardVariant, ContainerTokens> = {
  bold: {
    bg: "#000",
    fg: "#fff",
    muted: "rgba(255,255,255,0.55)",
    rule: "rgba(255,255,255,0.14)",
  },
  // Plan-2-Backlog: subtle bleibt unimplementiert, Werte reserviert.
  subtle: {
    bg: "#efece4",
    fg: "#1a1a1a",
    muted: "rgba(26,26,26,0.55)",
    rule: "rgba(26,26,26,0.12)",
  },
};

export function UpNextInline({
  items,
  wide = true,
  colophonStyle = "bold",
  lang = 'de',
}: UpNextInlineProps) {
  const tokens = CONTAINER_TOKENS[colophonStyle];

  // Block 2.5 TEIL C: keine Items → Rail ausblenden.
  // Briefing-Zitat: "Wenn keine Nachbarn vorhanden: Rail leer lassen
  // oder ausblenden, NICHT mit Dummy füllen."
  if (!items || items.length === 0) return null;

  // Mockup-Pattern Z. 2023: SCROLLYS.slice(1, 6) — max 5 Cards.
  const railItems = items.slice(0, 5);

  return (
    <div
      style={{
        background: tokens.bg,
        color: tokens.fg,
        padding: wide ? "80px 60px 120px" : "48px 24px 80px",
        borderTop: `1px solid ${tokens.rule}`,
      }}
    >
      {/* Header-Row: Eyebrow + H3 left, Count right */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: wide ? 28 : 20,
          gap: 16,
          maxWidth: 1200,
          margin: `0 auto ${wide ? 28 : 20}px`,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: PINK,
              marginBottom: 8,
            }}
          >
            {t('upnext.eyebrow', lang)}
          </div>
          <h3
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: wide ? 44 : 32,
              fontWeight: 400,
              margin: 0,
              letterSpacing: -0.8,
              lineHeight: 1.0,
            }}
          >
            {t('upnext.heading', lang)}
          </h3>
        </div>
        {/* Plan-2-Backlog: "picks →" Click-Handler (z.B. → /explore mit
            Filter on category) */}
        <div
          style={{
            fontSize: 12,
            color: tokens.muted,
            whiteSpace: "nowrap",
          }}
        >
          {railItems.length} picks →
        </div>
      </div>

      {/* Horizontal Rail: gridAutoFlow column + overflow-x scroll +
          negative-margin bleed für edge-aligned cards. */}
      <div
        style={{
          display: "grid",
          gridAutoFlow: "column",
          gridAutoColumns: wide ? "minmax(220px, 260px)" : "minmax(160px, 180px)",
          gap: wide ? 20 : 14,
          overflowX: "auto",
          scrollbarWidth: "none",
          paddingBottom: 8,
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        {railItems.map((s, i) => (
          <UpNextCard
            key={s.id}
            scrolly={s}
            index={i}
            variant={colophonStyle}
          />
        ))}
      </div>
    </div>
  );
}
